import React, { useMemo, useState } from 'react'
import { Box, CardMedia, Typography } from '@mui/material'
import LocationAccessImage from '../../../../assets/images/location-access.svg'
import AdxButton from 'components/custom/AdxButton'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'

const isiPhone = () => {
  if (typeof navigator === 'undefined') return false
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

const isStandalonePWA = () => {
  if (typeof window === 'undefined') return false
  // Safari A2HS
  const safariStandalone = window.navigator?.standalone === true
  // iOS/modern
  const displayModeStandalone = window.matchMedia?.('(display-mode: standalone)')?.matches
  return !!(safariStandalone || displayModeStandalone)
}

const LocationError = ({ props }) => {
  const [errorModal, setErrorModal] = useState(false)

  const onIOS = useMemo(() => isiPhone(), [])
  const onPWA = useMemo(() => isStandalonePWA(), [])

  const allowWithoutLocation = props?.allowWithoutLocation === true

  const handleContinueWithoutLocation = () => {
    setErrorModal(false)
    props.setPage(1)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          marginBottom: 'auto',
        }}
      >
        <CardMedia
          component="img"
          alt="location access error image"
          sx={{ height: 180, width: 180 }}
          src={LocationAccessImage}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            textAlign: 'center',
          }}
        >
          <Typography variant="h4">Location access needed</Typography>

          <Typography sx={{ color: 'light-gray-text.main' }}>
            We can’t detect your location yet. Please enable location so your attendance can be recorded accurately.
          </Typography>

          {onIOS ? (
            <Box sx={{ textAlign: 'left', width: '100%' }}>
              <Typography sx={{ color: 'light-gray-text.main', fontWeight: 600, mb: 1 }}>
                iPhone steps (Safari / Chrome):
              </Typography>
              <ul style={{ marginTop: 0 }}>
                <li>
                  <Typography sx={{ color: 'light-gray-text.main' }}>
                    Settings → Privacy & Security → Location Services → ON
                  </Typography>
                </li>
                <li>
                  <Typography sx={{ color: 'light-gray-text.main' }}>
                    Settings → Safari → Location → Allow
                  </Typography>
                </li>
                <li>
                  <Typography sx={{ color: 'light-gray-text.main' }}>
                    In Safari: tap <b>aA</b> → Website Settings → Location → Allow
                  </Typography>
                </li>
                {onPWA && (
                  <li>
                    <Typography sx={{ color: 'light-gray-text.main' }}>
                      If using “Add to Home Screen”, open the site once in Safari and allow Location there, then retry.
                    </Typography>
                  </li>
                )}
              </ul>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'left', width: '100%' }}>
              <ul style={{ marginTop: 0 }}>
                <li>
                  <Typography sx={{ color: 'light-gray-text.main' }}>
                    Check your device location settings and browser site permissions.
                  </Typography>
                </li>
                <li>
                  <Typography sx={{ color: 'light-gray-text.main' }}>
                    After allowing location, tap Retry.
                  </Typography>
                </li>
              </ul>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          padding: '24px',
          marginX: '-24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <AdxButton
          onClick={() => props.getUserLocation?.()}
          sx={{ width: '100%', boxShadow: 'none' }}
        >
          Retry location
        </AdxButton>

        {/* optional fallback - only if client allows */}
        {allowWithoutLocation && (
          <AdxButton
            variant="outlined"
            onClick={() => setErrorModal(true)}
            sx={{ width: '100%', boxShadow: 'none' }}
          >
            Clock in without location
          </AdxButton>
        )}
      </Box>

      {allowWithoutLocation && (
        <LocationErrorModal
          errorModal={errorModal}
          setErrorModal={setErrorModal}
          onClick={handleContinueWithoutLocation}
        />
      )}
    </>
  )
}

export default LocationError

const LocationErrorModal = ({ errorModal, setErrorModal, onClick }) => {
  return (
    <AdxInfoModal
      modalWidth={550}
      muiIcon="wrong_location"
      open={errorModal}
      onClose={() => setErrorModal(false)}
      iconColor="error.main"
      title="Clock in without location"
      subtitle="This option is not recommended for your attendance records. However, if you're experiencing difficulties connecting to your location, you may proceed with clocking in without location data."
      footerButtons
      firstBtnLabel={'Continue'}
      firstBtnHandle={onClick}
      secondBtnLabel={'Cancel'}
      secondBtnHandle={() => setErrorModal(false)}
    />
  )
}